import { useEffect, useRef } from "react";
import { Loader2, MessageCircle, MessageSquare, Radio } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { underwritingService } from "@/services/UnderwritingService";
import { useApi } from "@/hooks/useApi";
import { MessageEntry } from "@/models/underwriting";

/** "15 Jul 2026, 14:02" — day, short month, year, then 24h time. */
function formatMessageDate(isoString: string): string {
  const d = new Date(isoString);
  const date = d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const time = d.toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${date}, ${time}`;
}

const MESSAGE_CHANNEL_ICONS: Record<MessageEntry["channel"], typeof MessageSquare> = {
  SMS: MessageSquare,
  WhatsApp: MessageCircle,
  RCS: Radio,
};

function MessageBubble({ message, onMediaLoad }: { message: MessageEntry; onMediaLoad?: () => void }) {
  const isOutbound = message.direction === "outbound";
  const hasMedia = message.mediaUrls.length > 0;

  return (
    <div className={cn("flex flex-col gap-1", isOutbound ? "items-end" : "items-start")}>
      <div
        className={cn(
          "max-w-[75%] rounded-2xl text-[12px] leading-relaxed shadow-sm",
          hasMedia ? "overflow-hidden p-1.5" : "whitespace-pre-wrap px-3.5 py-2.5",
          isOutbound
            ? "rounded-tr-sm bg-[#f4f1ff] text-foreground"
            : "rounded-tl-sm border border-border bg-background text-foreground"
        )}
      >
        {hasMedia && (
          <div className={cn("flex flex-col gap-1.5", message.body && "mb-2")}>
            {message.mediaUrls.map((url) => (
              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                <img
                  src={url}
                  alt="Message attachment"
                  className="max-h-64 w-full rounded-xl object-cover"
                  loading="lazy"
                  onLoad={onMediaLoad}
                />
              </a>
            ))}
          </div>
        )}
        {hasMedia ? (
          message.body && <span className="block whitespace-pre-wrap px-2 pb-1">{message.body}</span>
        ) : (
          message.body || <span className="italic opacity-60">(no text content)</span>
        )}
      </div>
      <span className="px-1 text-[10px] text-muted-foreground">{formatMessageDate(message.date)}</span>
    </div>
  );
}

/** Renders a list of messages and auto-scrolls itself to the bottom once loaded. */
function MessagesList({
  messages,
  emptyLabel,
  scrollContainerRef,
}: {
  messages: MessageEntry[];
  emptyLabel: string;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  // Tracks whether the scroll container was resting at the bottom, so late-loading media
  // (which grows the content height after we've already scrolled down) can re-snap to it
  // without yanking the view if the user has since scrolled up to read older messages.
  const isAtBottomRef = useRef(true);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
    isAtBottomRef.current = true;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 24;
    };
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [messages, scrollContainerRef]);

  const handleMediaLoad = () => {
    if (isAtBottomRef.current) bottomRef.current?.scrollIntoView({ block: "end" });
  };

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 py-10 text-center">
        <p className="text-[12px] font-medium text-foreground/70">{emptyLabel}</p>
        <p className="text-[11px] text-muted-foreground">Nothing to show here yet</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onMediaLoad={handleMediaLoad} />
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

function ChannelTabTrigger({
  value,
  label,
  count,
}: {
  value: MessageEntry["channel"];
  label: string;
  count: number;
}) {
  const Icon = MESSAGE_CHANNEL_ICONS[value];
  return (
    <TabsTrigger value={value} className="gap-1.5 px-3 py-1.5 text-[12px]">
      <Icon className="size-3.5" />
      {label}
      <span
        className={cn(
          "ml-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
          "bg-foreground/10 text-foreground/70 data-[state=active]:bg-primary/15 data-[state=active]:text-primary"
        )}
      >
        {count}
      </span>
    </TabsTrigger>
  );
}

/** Messages tab content: SMS / WhatsApp / RCS conversation history, each in its own sub-tab. */
export function MessagesTab({ applicationId }: { applicationId: number | null }) {
  const smsScrollRef = useRef<HTMLDivElement>(null);
  const whatsAppScrollRef = useRef<HTMLDivElement>(null);
  const rcsScrollRef = useRef<HTMLDivElement>(null);

  const messagesResult = useApi(
    (signal) => underwritingService.getMessages(applicationId!, signal),
    [applicationId],
    { skip: applicationId == null }
  );

  const messages = messagesResult.data ?? [];
  const smsMessages = messages.filter((message) => message.channel === "SMS");
  const whatsAppMessages = messages.filter((message) => message.channel === "WhatsApp");
  const rcsMessages = messages.filter((message) => message.channel === "RCS");

  return (
    <div className="flex h-full flex-col">
      {messagesResult.loading && (
        <div className="flex flex-1 items-center justify-center gap-2 py-4 text-[11px] text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading messages…
        </div>
      )}

      {!messagesResult.loading && messagesResult.error && (
        <p className="px-4 py-3 text-[11px] text-rose-600">{messagesResult.error}</p>
      )}

      {!messagesResult.loading && !messagesResult.error && (
        <Tabs defaultValue="SMS" className="flex min-h-0 flex-1 flex-col gap-0">
          <div className="shrink-0 border-b border-border px-4 py-2.5">
            <TabsList className="h-auto w-fit gap-1 bg-muted/60 p-1">
              <ChannelTabTrigger value="SMS" label="SMS" count={smsMessages.length} />
              <ChannelTabTrigger value="WhatsApp" label="WhatsApp" count={whatsAppMessages.length} />
              <ChannelTabTrigger value="RCS" label="RCS" count={rcsMessages.length} />
            </TabsList>
          </div>
          <TabsContent
            ref={smsScrollRef}
            value="SMS"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            <MessagesList messages={smsMessages} emptyLabel="No SMS messages" scrollContainerRef={smsScrollRef} />
          </TabsContent>
          <TabsContent
            ref={whatsAppScrollRef}
            value="WhatsApp"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            <MessagesList
              messages={whatsAppMessages}
              emptyLabel="No WhatsApp messages"
              scrollContainerRef={whatsAppScrollRef}
            />
          </TabsContent>
          <TabsContent
            ref={rcsScrollRef}
            value="RCS"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            <MessagesList messages={rcsMessages} emptyLabel="No RCS messages" scrollContainerRef={rcsScrollRef} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
